import React, { useMemo, useCallback, useRef, useEffect, useState } from 'react';
import { Dropdown } from 'react-bootstrap';
import { IconDropdownProps } from './IconDropdown.types';
import styles from './IconDropdown.module.scss';

/**
 * Generic dropdown component with optional icon rendering support.
 *
 * Built on react-bootstrap Dropdown for consistency with existing UI patterns.
 * Supports keyboard navigation, ARIA accessibility, and custom icon rendering.
 *
 * @example
 * // Text-only dropdown
 * <IconDropdown
 *   options={items}
 *   value={selected}
 *   onChange={handleChange}
 *   getOptionValue={(opt) => opt.id}
 *   getOptionLabel={(opt) => opt.name}
 * />
 *
 * @example
 * // Dropdown with icons
 * <IconDropdown
 *   options={items}
 *   value={selected}
 *   onChange={handleChange}
 *   getOptionValue={(opt) => opt.id}
 *   getOptionLabel={(opt) => opt.name}
 *   renderIcon={(opt) => <Icon name={opt.icon} />}
 * />
 */
function IconDropdown<T>({
    options,
    value,
    onChange,
    getOptionValue,
    getOptionLabel,
    renderIcon,
    disabled = false,
    className = '',
    placeholder = 'Select an option',
    ariaLabel,
    maxHeight = '400px',
    testId = 'icon-dropdown',
}: IconDropdownProps<T>): React.ReactElement {
    const [isOpen, setIsOpen] = useState(false);
    const [focusedIndex, setFocusedIndex] = useState(-1);
    const menuRef = useRef<HTMLDivElement>(null);
    const itemRefs = useRef<(HTMLElement | null)[]>([]);

    // Find the currently selected option
    const selectedOption = useMemo(() => {
        return options.find((opt) => getOptionValue(opt) === value);
    }, [options, value, getOptionValue]);

    // Memoize the selected label to avoid recalculation
    const selectedLabel = useMemo(() => {
        return selectedOption ? getOptionLabel(selectedOption) : placeholder;
    }, [selectedOption, getOptionLabel, placeholder]);

    // Memoize the selected icon
    const selectedIcon = useMemo(() => {
        if (!renderIcon || !selectedOption) return null;
        return renderIcon(selectedOption);
    }, [renderIcon, selectedOption]);

    // Handle item selection
    const handleSelect = useCallback(
        (optionValue: string | number) => {
            onChange(optionValue);
            setIsOpen(false);
        },
        [onChange]
    );

    // Handle dropdown toggle
    const handleToggle = useCallback((nextShow: boolean) => {
        setIsOpen(nextShow);
        if (nextShow) {
            // Find the index of the currently selected item
            const selectedIndex = options.findIndex(
                (opt) => getOptionValue(opt) === value
            );
            setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0);
        } else {
            setFocusedIndex(-1);
        }
    }, [options, getOptionValue, value]);

    // Handle keyboard navigation
    const handleKeyDown = useCallback(
        (event: React.KeyboardEvent) => {
            if (!isOpen) {
                if (event.key === 'ArrowDown' || event.key === 'ArrowUp' || event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    setIsOpen(true);
                    const selectedIndex = options.findIndex(
                        (opt) => getOptionValue(opt) === value
                    );
                    setFocusedIndex(selectedIndex >= 0 ? selectedIndex : 0);
                }
                return;
            }

            switch (event.key) {
                case 'ArrowDown':
                    event.preventDefault();
                    setFocusedIndex((prev) =>
                        prev < options.length - 1 ? prev + 1 : 0
                    );
                    break;
                case 'ArrowUp':
                    event.preventDefault();
                    setFocusedIndex((prev) =>
                        prev > 0 ? prev - 1 : options.length - 1
                    );
                    break;
                case 'Enter':
                case ' ':
                    event.preventDefault();
                    if (focusedIndex >= 0 && focusedIndex < options.length) {
                        handleSelect(getOptionValue(options[focusedIndex]));
                    }
                    break;
                case 'Escape':
                    event.preventDefault();
                    setIsOpen(false);
                    break;
                case 'Tab':
                    setIsOpen(false);
                    break;
                default:
                    break;
            }
        },
        [isOpen, options, focusedIndex, getOptionValue, handleSelect, value]
    );

    // Scroll focused item into view
    useEffect(() => {
        if (isOpen && focusedIndex >= 0 && itemRefs.current[focusedIndex]) {
            // scrollIntoView may not be available in test environments (jsdom)
            const element = itemRefs.current[focusedIndex];
            if (element && typeof element.scrollIntoView === 'function') {
                element.scrollIntoView({
                    block: 'nearest',
                    behavior: 'smooth',
                });
            }
        }
    }, [isOpen, focusedIndex]);

    // Reset item refs when options change
    useEffect(() => {
        itemRefs.current = itemRefs.current.slice(0, options.length);
    }, [options.length]);

    return (
        <Dropdown
            show={isOpen}
            onToggle={handleToggle}
            className={`${styles.iconDropdown} ${className}`}
            data-testid={testId}
        >
            <Dropdown.Toggle
                variant="outline-secondary"
                disabled={disabled}
                className={`${styles.toggle} w-100 d-flex align-items-center justify-content-between`}
                aria-label={ariaLabel}
                onKeyDown={handleKeyDown}
                data-testid={`${testId}-toggle`}
            >
                <span className={`${styles.selectedContent} d-flex align-items-center`}>
                    {selectedIcon && (
                        <span className={styles.iconWrapper} data-testid={`${testId}-selected-icon`}>
                            {selectedIcon}
                        </span>
                    )}
                    <span className={`${styles.label} text-truncate`}>
                        {selectedLabel}
                    </span>
                </span>
            </Dropdown.Toggle>

            <Dropdown.Menu
                ref={menuRef}
                className={`${styles.menu} w-100`}
                style={{ maxHeight, overflowY: 'auto' }}
                onKeyDown={handleKeyDown}
                data-testid={`${testId}-menu`}
            >
                {options.map((option, index) => {
                    const optionValue = getOptionValue(option);
                    const optionLabel = getOptionLabel(option);
                    const isSelected = optionValue === value;
                    const isFocused = index === focusedIndex;
                    const icon = renderIcon ? renderIcon(option) : null;

                    return (
                        <Dropdown.Item
                            key={String(optionValue)}
                            ref={(el: HTMLElement | null) => {
                                itemRefs.current[index] = el;
                            }}
                            active={isSelected}
                            className={`${styles.item} d-flex align-items-center ${
                                isFocused ? styles.focused : ''
                            }`}
                            onClick={() => handleSelect(optionValue)}
                            aria-selected={isSelected}
                            data-testid={`${testId}-item-${optionValue}`}
                        >
                            {icon && (
                                <span className={styles.iconWrapper}>
                                    {icon}
                                </span>
                            )}
                            <span className={styles.itemLabel}>{optionLabel}</span>
                        </Dropdown.Item>
                    );
                })}
            </Dropdown.Menu>
        </Dropdown>
    );
}

export default IconDropdown;
